 "use client";

import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { IUserFull, ITeam, TacticApiClient, ResGetUser } from '@gettactic/api';
import { useTeams } from './../useTeams';
import { useTeamMembers } from './../useTeamMembers';
import { useUserTeamsList } from './../useUserTeams';

export type User = IUserFull;
export type Team = ITeam;
export type TeamsResponse = {
  elements: Team[];
};

export function useDefaultGroupMembers(api: TacticApiClient) {
    const queryClient = useQueryClient();
    const { teamList: groups } = useTeams({ type: 'group', hidden: false, api });
    const { users: groupMembers } = useTeamMembers(
      groups?.elements[0]?.id ?? null,
      'group',
      null,
      api
    );
    const mutateDefaultGroupMembers = async () => {
      queryClient.invalidateQueries({ 
        queryKey: ['organizations.teamsWithOptions', 'group', false] 
      });
      queryClient.invalidateQueries({ queryKey: ['teams.members'] });
    };
    return {
      defaultGroupMembers: groupMembers,
      defaultMutateGroupMembers: mutateDefaultGroupMembers
    };
  }
  
  export function useFavs(user: IUserFull | ResGetUser, api: TacticApiClient, issueSuccessMessage: (message:string) => void) {
    const queryClient = useQueryClient();
    const { userTeamList: userGroups } = useUserTeamsList(user.id, 'group', api);
    const { teamList: groups } = useTeams({ type: 'group', hidden: false, api });
    const { defaultGroupMembers, defaultMutateGroupMembers } =
      useDefaultGroupMembers(api);

    const mutateAll = async () => {
      queryClient.invalidateQueries({ queryKey: ['teams.forUser'] });
      queryClient.invalidateQueries({ 
        queryKey: ['organizations.teamsWithOptions', 'group', false] 
      });
      await defaultMutateGroupMembers();
    };
    const [open, setOpen] = useState(false);
    const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
    const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);
    const [arrowElement, setArrowElement] = useState<HTMLElement | null>(null);

    const userHasAnyFav =
      !!userGroups && userGroups.elements && userGroups.elements.length > 0;
    const isUserInDefaultGroup =
      !!defaultGroupMembers &&
      !!defaultGroupMembers.elements.find((x: any) => x.id === user.id);
    const [hasAnyFav, setHasAnyFav] = useState(userHasAnyFav);
  
    useEffect(() => {
      setHasAnyFav(userHasAnyFav);
    }, [userHasAnyFav]);
  
    const addUserToDefaultGroup = async (api?: TacticApiClient) => {
      if (!api) {
        console.error('API instance must be provided to useFavs hook for addUserToDefaultGroup');
        return;
      }

      if (!isUserInDefaultGroup) {
        const defaultGroup = groups?.elements.length
          ? groups.elements[0]
          : (
              await api.client.teams.add({
                name: 'My Favorites',
                parent_id: null,
                type: 'group'
              })
            )?.result;
        if (!defaultGroup) {
          throw new Error('Sorry we couldn\'t save your user');
        }
        await api.client.teams.addMember({
          teamId: defaultGroup.id,
          userId: user.id,
          type: 'group'
        });
      }
      setHasAnyFav(true);
      await mutateAll();
      issueSuccessMessage('Added to your favorites!');
    };
    const removeUserFromDefaultGroup = async (api?: TacticApiClient) => {
      if (!api) {
        console.error('API instance must be provided to useFavs hook for removeUserFromDefaultGroup');
        return;
      }
      if (!groups) {
        return;
      }
      if (isUserInDefaultGroup) {
        const defaultGroup = groups?.elements?.length > 0
            ? groups.elements[0]
            : null;
        if (!defaultGroup) {
          throw new Error('Error trying to get the default Group');
        }
        issueSuccessMessage('Removed from your favorites!');
        await api.client.teams.removeMember({
          teamId: defaultGroup.id,
          userId: user.id,
          type: 'group'
        });
      }
      await mutateAll();
    };
  
    const addUserToGroup = async (group: ITeam, api?: TacticApiClient) => {
      if (!api) {
        console.error('API instance must be provided to useFavs hook for addUserToGroup');
        return;
      }
      if (!groups) {
        return;
      }
      await api.client.teams.addMember({
        teamId: group.id,
        userId: user.id,
        type: 'group'
      });
      setHasAnyFav(true);
      await mutateAll();
      issueSuccessMessage('Added to your favorites!');
    };
    const removeUserFromGroup = async (group: ITeam, api?: TacticApiClient) => {
      if (!api) {
        console.error('API instance must be provided to useFavs hook for removeUserFromGroup');
        return;
      }
      if (!groups) {
        return;
      }
      await api.client.teams.removeMember({
        teamId: group.id,
        userId: user.id,
        type: 'group'
      });
      await mutateAll();
      issueSuccessMessage('Removed from your favorites!');
    };
  
    return {
      open,
      setOpen,
      popperElement,
      setPopperElement,
      referenceElement,
      setReferenceElement,
      arrowElement,
      setArrowElement,
      hasAnyFav,
      removeUserFromDefaultGroup,
      addUserToDefaultGroup,
      addUserToGroup,
      removeUserFromGroup,
      groups,
      userGroups
    };
  }
  
  export default useFavs;
  